import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import RichTextEditor from '../../components/common/RichTextEditor';
import ImagePickerModal from '../../components/common/ImagePickerModal';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  ArrowLeft, BookOpen, Plus, Trash2, Send, Check, X,
  FileText, Clock, ChevronRight, ChevronDown, AlertCircle, 
  Loader2, RefreshCw, FolderOpen, CheckCircle, AlertTriangle, Flag, FolderInput,
  GripVertical, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { TopicPicker as TopicPickerTree } from '../../components/shared/TopicPicker';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Grade configurations
const DEPARTMENT_GRADES = {
  Engineering: ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO'],
  Deck: ['Master', 'Chief Mate', '2MFG']
};

// Status colors
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  pending_review: 'bg-amber-100 text-amber-700',
  published: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'Pending',
  published: 'Published',
  rejected: 'Rejected'
};

// Sortable chapter row wrapper for drag-and-drop reordering.
// Wraps a single chapter card and provides a left-hand grip handle.
// The whole card remains click-to-edit; only the grip starts a drag.
const SortableChapterRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch gap-1">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex items-center px-1 text-gray-300 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none"
        title="Drag to reorder"
        data-testid={`chapter-drag-handle-${id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

const NotesManagementPage = () => {
  const { department } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const isFullAdmin = user?.role === 'admin';
  const isSubAdmin = user?.role === 'sub_admin';
  
  const departmentName = department === 'engineering' ? 'Engineering' : 'Deck';
  const grades = DEPARTMENT_GRADES[departmentName] || [];

  // Get URL search params for grade/noteId from Flagged Reports navigation
  const [searchParams] = useSearchParams();
  const urlGrade = searchParams.get('grade');
  const urlNoteId = searchParams.get('noteId');
  
  // State
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState(urlGrade || grades[0] || '');
  const [functions, setFunctions] = useState([]);
  // iter 108e: flat topics list for the reassign picker (shared
  // TopicPickerTree needs a flat array — builds the tree itself). Grade-
  // scoped so admins don't accidentally reassign a Class 2 note under a
  // Class 1 topic.
  const [flatTopicsForGrade, setFlatTopicsForGrade] = useState([]);
  const [expandedFunctions, setExpandedFunctions] = useState({});
  const [expandedTopics, setExpandedTopics] = useState({}); // For parent topics with sub-topics
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedTopicWithSubs, setSelectedTopicWithSubs] = useState(null); // Parent topic + its subtopics
  const [topicNotes, setTopicNotes] = useState([]); // Notes grouped by topic/subtopic
  const [stats, setStats] = useState(null);
  
  // Pending review
  const [pendingReviewOpen, setPendingReviewOpen] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);
  
  // Note editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [selectedSubTopicForNew, setSelectedSubTopicForNew] = useState(null); // Which subtopic to add chapter to
  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    related_note_ids: []
  });
  // Full picker list (grade-scoped, cross-function) — loaded once when the
  // editor opens, filtered client-side by the admin's typing.
  const [pickerNotes, setPickerNotes] = useState([]);
  const [pickerFilter, setPickerFilter] = useState('');
  const [saving, setSaving] = useState(false);
  // When admin reassigns a note to a different topic via the edit dialog
  // we surface a Reassign sub-panel. Pending until saved (so accidental
  // picks don't immediately move the note).
  const [reassignTopicId, setReassignTopicId] = useState('');
  const [showReassignPicker, setShowReassignPicker] = useState(false);
  
  // Dialogs
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [noteToReject, setNoteToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Image picker
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imageCallback, setImageCallback] = useState(null);
  
  // Flagged reports
  const [flaggedCounts, setFlaggedCounts] = useState({ total_pending: 0, by_note: {} });
  const [noteReports, setNoteReports] = useState({}); // { note_id: [reports] }
  const [expandedReports, setExpandedReports] = useState({}); // Which notes have reports expanded

  // Fetch flagged counts (simplified - just for the badge)
  const fetchFlaggedCounts = useCallback(async () => {
    try {
      const params = { grade: selectedGrade };
      const response = await axios.get(`${API}/notes/reports/counts`, { params });
      setFlaggedCounts(response.data);
    } catch (error) {
      console.error('Failed to fetch flagged counts:', error);
    }
  }, [selectedGrade]);
  
  // Fetch reports for a specific note
  const fetchNoteReports = useCallback(async (noteId) => {
    try {
      const response = await axios.get(`${API}/notes/${noteId}/reports`, {
        params: { status: 'pending' }
      });
      setNoteReports(prev => ({ ...prev, [noteId]: response.data.reports }));
    } catch (error) {
      console.error('Failed to fetch note reports:', error);
    }
  }, []);
  
  // Update report status (resolve/dismiss)
  const updateReportStatus = useCallback(async (reportId, status, resolutionNote = '') => {
    try {
      await axios.put(`${API}/notes/reports/${reportId}/status`, {
        status,
        resolution_note: resolutionNote
      });
      toast.success(`Report ${status}`);
      // Refresh counts and reports
      fetchFlaggedCounts();
      // Remove from local state
      setNoteReports(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(noteId => {
          updated[noteId] = updated[noteId].filter(r => r.id !== reportId);
        });
        return updated;
      });
    } catch (error) {
      toast.error('Failed to update report');
    }
  }, [fetchFlaggedCounts]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notes/stats/${departmentName}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [departmentName]);

  // Build topic tree with parent-child relationships
  const buildTopicTree = (topics) => {
    const topicMap = {};
    const rootTopics = [];
    
    // First pass: create map
    topics.forEach(topic => {
      topicMap[topic.id] = { ...topic, children: [] };
    });
    
    // Second pass: build tree
    topics.forEach(topic => {
      if (topic.parent_topic_id && topicMap[topic.parent_topic_id]) {
        topicMap[topic.parent_topic_id].children.push(topicMap[topic.id]);
      } else if (!topic.parent_topic_id) {
        rootTopics.push(topicMap[topic.id]);
      }
    });
    
    return rootTopics;
  };

  // Fetch functions with topics and note counts (optimized)
  const fetchFunctionsWithTopics = useCallback(async () => {
    if (!selectedGrade) return;
    
    try {
      setLoading(true);
      
      // Get functions for selected grade (filter to only oral functions - written functions use oral topics)
      const funcResponse = await axios.get(`${API}/functions`, { 
        params: { grade: selectedGrade, exam_type: 'orals' } 
      });
      const funcs = funcResponse.data || [];
      
      // Get all note counts in one call
      const countsResponse = await axios.get(`${API}/notes/counts-by-topic`, {
        params: { grade: selectedGrade }
      });
      const noteCounts = countsResponse.data.counts || {};
      
      // For each function, get topics
      const functionsWithTopics = await Promise.all(
        funcs.map(async (func) => {
          // Get topics
          const topicsResponse = await axios.get(`${API}/topics`, {
            params: { function_id: func.id, grade: selectedGrade }
          });
          const topics = topicsResponse.data || [];
          
          // Add note counts from our pre-fetched data
          const topicsWithCounts = topics.map(topic => ({
            ...topic,
            noteCount: noteCounts[topic.id] || 0
          }));
          
          // Build topic tree
          const topicTree = buildTopicTree(topicsWithCounts);
          
          // Calculate totals (including subtopics)
          const calculateTotals = (topics) => {
            let totalNotes = 0;
            let topicsWithNotes = 0;
            let totalTopics = 0;
            
            topics.forEach(t => {
              totalTopics++;
              totalNotes += t.noteCount;
              if (t.noteCount > 0) topicsWithNotes++;
              
              if (t.children?.length > 0) {
                const childTotals = calculateTotals(t.children);
                totalNotes += childTotals.totalNotes;
                topicsWithNotes += childTotals.topicsWithNotes;
                totalTopics += childTotals.totalTopics;
              }
            });
            
            return { totalNotes, topicsWithNotes, totalTopics };
          };
          
          const totals = calculateTotals(topicTree);
          
          return {
            ...func,
            topics: topicsWithCounts, // flat list for lookups
            topicTree, // hierarchical for display
            ...totals
          };
        })
      );
      
      setFunctions(functionsWithTopics);
      
      // Auto-expand first function with notes
      if (functionsWithTopics.length > 0) {
        const firstWithNotes = functionsWithTopics.find(f => f.totalNotes > 0);
        if (firstWithNotes) {
          setExpandedFunctions({ [firstWithNotes.id]: true });
        }
      }
    } catch (error) {
      console.error('Failed to fetch functions:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedGrade]);

  // Fetch notes for a topic and its subtopics
  const fetchTopicNotes = useCallback(async (topic, allTopics) => {
    if (!topic?.id) {
      console.warn('No topic ID provided');
      return;
    }
    
    try {
      // Iter 105.5 fix: walk the FULL descendant subtree — not just
      // direct children — so chapters written under 3rd/4th/5th-level
      // topics still surface when the admin clicks any ancestor.
      // Prior behaviour was one-hop, which meant grandchild+ chapters
      // silently disappeared once topic trees went beyond 2 levels.
      const byParent = new Map();
      (allTopics || []).forEach((t) => {
        if (!t.parent_topic_id) return;
        if (!byParent.has(t.parent_topic_id)) byParent.set(t.parent_topic_id, []);
        byParent.get(t.parent_topic_id).push(t);
      });

      const descendants = []; // {topic, depth}
      const walk = (id, depth) => {
        (byParent.get(id) || []).forEach((child) => {
          descendants.push({ topic: child, depth });
          walk(child.id, depth + 1);
        });
      };
      walk(topic.id, 1);

      const topicIds = [topic.id, ...descendants.map((d) => d.topic.id)];

      // Fetch notes for the parent + every descendant in parallel.
      const notesPromises = topicIds.map((id) =>
        axios.get(`${API}/notes/by-topic/${id}`).then((res) => ({
          topicId: id,
          notes: res.data.notes || [],
        })).catch((err) => {
          console.error(`Failed to fetch notes for topic ${id}:`, err);
          return { topicId: id, notes: [] };
        })
      );

      const results = await Promise.all(notesPromises);
      const byId = new Map(results.map((r) => [r.topicId, r.notes]));

      // Structure: parent first, then descendants depth-first so the
      // reader sees the tree order they'd expect when browsing.
      const structured = [];
      const parentNotes = byId.get(topic.id) || [];
      structured.push({ topicId: topic.id, topicName: topic.name, notes: parentNotes });
      descendants.forEach(({ topic: child, depth }) => {
        const childNotes = byId.get(child.id) || [];
        // Only include descendants that actually have chapters to
        // avoid cluttering the middle pane with 20 empty sections.
        if (childNotes.length > 0) {
          structured.push({
            topicId: child.id,
            topicName: child.name,
            notes: childNotes,
            isSubTopic: true,
            depth,
          });
        }
      });

      setTopicNotes(structured);
      setSelectedTopic(topic);
      // Preserve the shape expected by the reassignment picker: children
      // remains the flat descendant list for backward compatibility with
      // downstream consumers that iterate it.
      setSelectedTopicWithSubs({ parent: topic, children: descendants.map((d) => d.topic) });
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      toast.error('Failed to load chapters');
    }
  }, []);

  // Fetch pending reviews
  const fetchPendingReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notes/pending-review/${departmentName}`);
      setPendingReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch pending reviews:', error);
    }
  }, [departmentName]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (selectedGrade) {
      fetchFunctionsWithTopics();
      fetchFlaggedCounts();
      setSelectedTopic(null);
      setTopicNotes([]);
      // iter 108e: pull the flat topic list for the reassign picker.
      // Cheap (one HTTP call, no aggregation) and keeps the picker
      // instantly interactive when the admin toggles it open.
      axios
        .get(`${API}/topics`, { params: { grade: selectedGrade } })
        .then((r) => setFlatTopicsForGrade(r.data || []))
        .catch(() => setFlatTopicsForGrade([]));
    }
    // Note: Only depend on selectedGrade to avoid resetting topic when fetchFlaggedCounts changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGrade]);

  // Handle opening a specific note from URL params (e.g., from Flagged Reports page)
  useEffect(() => {
    const openNoteFromUrl = async () => {
      if (urlNoteId && !loading && functions.length > 0) {
        try {
          // Fetch the note to get its topic info
          const response = await axios.get(`${API}/notes/${urlNoteId}`);
          const note = response.data;
          
          if (note) {
            // Find the function and topic for this note
            const func = functions.find(f => f.id === note.function_id);
            if (func) {
              // Expand the function
              setExpandedFunctions(prev => ({ ...prev, [func.id]: true }));
              
              // Find and select the topic
              const topic = func.topics?.find(t => t.id === note.topic_id);
              if (topic) {
                // Fetch notes for this topic
                await fetchTopicNotes(topic, func.topics);
                
                // Open the note editor
                setTimeout(() => {
                  openEditNoteEditor(note);
                }, 500);
              }
            }
          }
        } catch (error) {
          console.error('Failed to open note from URL:', error);
        }
        
        // Clear the URL params after processing
        window.history.replaceState({}, '', `/admin/notes/${department}`);
      }
    };
    
    openNoteFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlNoteId, loading, functions]);

  // Toggle function expansion
  const toggleFunction = (funcId) => {
    setExpandedFunctions(prev => ({
      ...prev,
      [funcId]: !prev[funcId]
    }));
  };

  // Toggle parent topic expansion (for subtopics)
  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  // Open editor for new note
  const openNewNoteEditor = (subTopic = null) => {
    if (!selectedTopic) {
      toast.error('Please select a topic first');
      return;
    }
    setEditingNote(null);
    setSelectedSubTopicForNew(subTopic);
    setNoteForm({ title: '', content: '', excerpt: '', related_note_ids: [] });
    setPickerFilter('');
    setEditorOpen(true);
  };

  // Open editor for existing note (click on chapter card)
  const openEditNoteEditor = (note) => {
    setEditingNote(note);
    setSelectedSubTopicForNew(null);
    setNoteForm({
      title: note.title,
      content: note.content,
      excerpt: note.excerpt || '',
      related_note_ids: note.related_note_ids || []
    });
    setReassignTopicId('');
    setShowReassignPicker(false);
    setPickerFilter('');
    setEditorOpen(true);
  };

  // Load the "Also Read" picker list once when the editor opens. Grade-scoped
  // and cross-function on purpose. Excludes the note being edited so it can't
  // reference itself.
  useEffect(() => {
    if (!editorOpen || !selectedGrade) return;
    let cancelled = false;
    (async () => {
      try {
        const params = { grade: selectedGrade };
        if (editingNote?.id) params.exclude_id = editingNote.id;
        const res = await axios.get(`${API}/notes/picker/list`, { params });
        if (!cancelled) setPickerNotes(res.data.items || []);
      } catch {
        if (!cancelled) setPickerNotes([]);
      }
    })();
    return () => { cancelled = true; };
  }, [editorOpen, selectedGrade, editingNote?.id]);

  // Save note
  const handleSaveNote = async () => {
    if (!noteForm.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      setSaving(true);
      
      if (editingNote) {
        const putPayload = {
          title: noteForm.title,
          content: noteForm.content,
          content_format: 'html',
          excerpt: noteForm.excerpt,
          related_note_ids: noteForm.related_note_ids || [],
        };
        // Only include topic_id when admin explicitly picked a destination
        // — empty/unchanged means "leave topic alone" so the backend's
        // change-only validation path isn't triggered for normal edits.
        if (reassignTopicId && reassignTopicId !== editingNote.topic_id) {
          putPayload.topic_id = reassignTopicId;
        }
        await axios.put(`${API}/notes/${editingNote.id}`, putPayload);
        toast.success(putPayload.topic_id ? 'Chapter updated and moved' : 'Chapter updated');
      } else {
        // Determine which topic to add to
        const targetTopicId = selectedSubTopicForNew?.id || selectedTopic.id;
        const func = functions.find(f => f.topics.some(t => t.id === targetTopicId));
        
        await axios.post(`${API}/notes`, {
          title: noteForm.title,
          department: departmentName,
          grade: selectedGrade,
          function_id: func?.id || selectedTopic.function_id,
          topic_id: targetTopicId,
          content: noteForm.content,
          content_format: 'html',
          excerpt: noteForm.excerpt,
          related_note_ids: noteForm.related_note_ids || [],
        });
        toast.success('Chapter created');
      }

      setEditorOpen(false);
      
      // Refresh notes for current topic only (lighter refresh)
      if (selectedTopic) {
        const func = functions.find(f => f.topics.some(t => t.id === selectedTopic.id));
        if (func) {
          fetchTopicNotes(selectedTopic, func.topics);
        }
      }
      // Defer heavy reload to avoid race conditions
      setTimeout(() => {
        fetchStats();
      }, 500);
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Delete note
  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      await axios.delete(`${API}/notes/${noteToDelete.id}`);
      toast.success('Chapter deleted');
      setDeleteConfirmOpen(false);
      setNoteToDelete(null);
      
      const func = functions.find(f => f.topics.some(t => t.id === selectedTopic.id));
      if (func) {
        fetchTopicNotes(selectedTopic, func.topics);
      }
      fetchFunctionsWithTopics();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete');
    }
  };

  // Submit for review
  const handleSubmitForReview = async (note, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/notes/${note.id}/status`, { status: 'pending_review' });
      toast.success('Submitted for review');
      const func = functions.find(f => f.topics.some(t => t.id === selectedTopic.id));
      if (func) {
        fetchTopicNotes(selectedTopic, func.topics);
      }
      fetchStats();
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error('Failed to submit');
    }
  };

  // Approve note
  const handleApproveNote = async (note, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/notes/${note.id}/status`, { status: 'published' });
      toast.success('Chapter published');
      const func = functions.find(f => f.topics.some(t => t.id === selectedTopic.id));
      if (func) {
        fetchTopicNotes(selectedTopic, func.topics);
      }
      fetchStats();
      fetchPendingReviews();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error('Failed to approve');
    }
  };

  // Reject note
  const handleRejectNote = async () => {
    if (!noteToReject || !rejectionReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    try {
      await axios.put(`${API}/notes/${noteToReject.id}/status`, { 
        status: 'rejected',
        rejection_reason: rejectionReason
      });
      toast.success('Chapter rejected');
      setRejectDialogOpen(false);
      setNoteToReject(null);
      setRejectionReason('');
      const func = functions.find(f => f.topics.some(t => t.id === selectedTopic.id));
      if (func) {
        fetchTopicNotes(selectedTopic, func.topics);
      }
      fetchStats();
      fetchPendingReviews();
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject');
    }
  };

  // Image picker handler - opens ImagePickerModal
  const handleImageSelect = (callback) => {
    setImageCallback(() => callback);
    setImagePickerOpen(true);
  };

  // ---- Drag & drop chapter reordering ----
  // Sensors: pointer + keyboard (accessibility). 6px activation distance
  // prevents accidental drags when clicking the row to open the editor.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Optimistic reorder within a single topic group.
  // - Reorders local state immediately for snappy UX
  // - PUT /api/notes/reorder persists 1-indexed order based on new list
  // - On failure, revert by re-fetching from server
  const handleChapterDragEnd = async (event, topicId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const groupIdx = topicNotes.findIndex(g => g.topicId === topicId);
    if (groupIdx === -1) return;

    const group = topicNotes[groupIdx];
    const oldIndex = group.notes.findIndex(n => n.id === active.id);
    const newIndex = group.notes.findIndex(n => n.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedNotes = arrayMove(group.notes, oldIndex, newIndex);

    // Optimistic update
    const nextTopicNotes = [...topicNotes];
    nextTopicNotes[groupIdx] = { ...group, notes: reorderedNotes };
    setTopicNotes(nextTopicNotes);

    try {
      await axios.put(`${API}/notes/reorder`, {
        note_ids: reorderedNotes.map(n => n.id),
      });
      toast.success('Chapter order saved');
    } catch (error) {
      console.error('Failed to reorder chapters:', error);
      toast.error('Failed to save new order — reverting');
      // Revert: re-fetch notes for the parent topic
      if (selectedTopic) {
        const func = functions.find(f => f.topics.some(t => t.id === selectedTopic.id));
        if (func) {
          fetchTopicNotes(selectedTopic, func.topics);
        }
      }
    }
  };

  // When image is selected from modal
  const handleImagePicked = (imageUrl) => {
    if (imageCallback && imageUrl) {
      imageCallback(imageUrl);
    }
    setImagePickerOpen(false);
    setImageCallback(null);
  };

  // Recursive rollup helper (iter 105.3): sum note counts across a
  // topic and every descendant. Prior implementation was one-hop, so
  // grandchild note counts never surfaced next to a grandparent — a
  // topic with all its chapters under grandchildren appeared "empty".
  const rollupNoteCount = (topic) => {
    const own = topic.noteCount || 0;
    const kids = (topic.children || []).reduce((s, c) => s + rollupNoteCount(c), 0);
    return own + kids;
  };

  // Get topic status indicator
  const getTopicIndicator = (topic) => {
    const totalNotes = rollupNoteCount(topic);
    if (totalNotes === 0) {
      return <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" title="No chapters" />;
    }
    return <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" title={`${totalNotes} chapters`} />;
  };

  // Render topic tree recursively
  const renderTopicTree = (topics, func, level = 0) => {
    return topics.map((topic) => {
      const hasChildren = topic.children && topic.children.length > 0;
      const isExpanded = expandedTopics[topic.id];
      const isSelected = selectedTopic?.id === topic.id;
      const ownNotes = topic.noteCount || 0;
      const totalNotes = rollupNoteCount(topic);
      const descendantNotes = totalNotes - ownNotes;
      
      // Check if topic has flagged notes (for badge display only)
      const topicHasFlagged = flaggedCounts.by_note && Object.keys(flaggedCounts.by_note).length > 0;
      
      return (
        <div key={topic.id}>
          <div
            className={`group flex items-start gap-2 pl-2 pr-4 py-2 rounded-lg cursor-pointer transition-colors ${
              isSelected 
                ? 'bg-[#14B8A6]/10 text-[#14B8A6]' 
                : 'hover:bg-gray-50 text-gray-700'
            }`}
            style={{ paddingLeft: `${8 + level * 16}px` }}
            onClick={() => {
              if (hasChildren) {
                toggleTopic(topic.id);
              }
              fetchTopicNotes(topic, func.topics);
            }}
          >
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              )
            ) : (
              <div className="w-4 flex-shrink-0" />
            )}
            <span className="flex-shrink-0 mt-0.5">{getTopicIndicator(topic)}</span>
            <div className="flex-1 min-w-0">
              <span
                className={`block text-sm min-w-0 leading-snug break-all line-clamp-2 ${level > 0 ? 'text-gray-600' : 'font-medium'}`}
                title={topic.name}
              >
                {topic.name}
              </span>
              {/* Depth hint (iter 105.3): when a parent has no direct
                  chapters but descendants do, surface it explicitly so
                  the admin knows to expand. Only rendered when the two
                  numbers differ AND the parent has children — otherwise
                  it's redundant noise. */}
              {hasChildren && ownNotes === 0 && descendantNotes > 0 && (
                <span className="block text-xs text-gray-400 mt-0.5">
                  0 here · {descendantNotes} in sub-topics
                </span>
              )}
            </div>
            {/* Right-side badge / copy-link — same slot, swapped on hover.
                Keeps the row width constant (badge width) so nothing ever
                overflows the sidebar. The chapter count is visible in the
                resting state; the inline-link icon replaces it while the
                row is hovered. Both are `flex-shrink-0` and vertically
                aligned to the first line of the topic name (mt-0.5) so the
                right cluster looks consistent whether the name wraps or not. */}
            <div className="relative flex-shrink-0 mt-0.5 h-[22px] min-w-[32px] flex items-center justify-end">
              <Badge variant="outline" className="text-xs group-hover:invisible">
                {totalNotes}
              </Badge>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(`[[topic:${topic.id}]]`)
                    .then(() => toast.success('Inline topic link copied'))
                    .catch(() => toast.error('Copy failed'));
                }}
                className="absolute inset-0 hidden group-hover:flex items-center justify-end text-sky-600 hover:text-sky-800"
                title="Copy inline link"
                data-testid={`copy-inline-topic-link-${topic.id}`}
              >
                <Link2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Render children if expanded */}
          {hasChildren && isExpanded && (
            <div>
              {renderTopicTree(topic.children, func, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <Layout>
      <div className="page-transition h-[calc(100vh-120px)]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/admin/content')}
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="p-2 bg-[#14B8A6]/10 rounded-lg">
              <BookOpen className="w-6 h-6 text-[#14B8A6]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{departmentName} Notes</h1>
              <p className="text-sm text-gray-500">
                {stats ? `${Object.values(stats.by_status || {}).reduce((a, b) => a + b, 0)} total chapters` : 'Loading...'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Grade Selector */}
            <Select value={selectedGrade} onValueChange={setSelectedGrade}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Select Grade" />
              </SelectTrigger>
              <SelectContent>
                {grades.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Pending Review Button */}
            {isFullAdmin && stats?.pending_review_count > 0 && (
              <Button 
                variant="outline" 
                onClick={() => { fetchPendingReviews(); setPendingReviewOpen(true); }}
                className="border-amber-500 text-amber-600"
              >
                <Clock className="w-4 h-4 mr-2" />
                Pending
                <Badge className="ml-2 bg-amber-500 text-white">{stats.pending_review_count}</Badge>
              </Button>
            )}
            
            {/* Flagged Reports Button - Always visible, navigate to dedicated page */}
            <Button 
              variant="outline"
              onClick={() => navigate('/admin/notes/flagged-reports')}
              className={flaggedCounts.total_pending > 0 
                ? "border-red-300 text-red-600 hover:bg-red-50" 
                : "border-gray-300 text-gray-600 hover:bg-gray-50"}
              data-testid="flagged-reports-button"
            >
              <Flag className="w-4 h-4 mr-2" />
              {flaggedCounts.total_pending > 0 
                ? `${flaggedCounts.total_pending} Flagged` 
                : 'Flagged Reports'}
            </Button>
            
            {/* Refresh */}
            <Button variant="outline" size="icon" onClick={fetchFunctionsWithTopics}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Two-Panel Layout */}
        <div className="flex gap-4 h-[calc(100%-60px)]">
          {/* Left Panel - Function & Topic Tree */}
          <Card className="w-80 flex-shrink-0 flex flex-col">
            <CardHeader className="py-3 px-4 border-b">
              <CardTitle className="text-sm font-medium text-gray-600">
                Functions & Topics
              </CardTitle>
            </CardHeader>
            <ScrollArea className="flex-1">
              {/* pr-4 reserves clearance for the overlay scrollbar so the
                  right-aligned count badges aren't covered when the list
                  overflows. The Radix scrollbar is ~10px wide and absolutely
                  positioned over the viewport. */}
              <div className="p-2 pr-5">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#14B8A6]" />
                  </div>
                ) : functions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No functions found for {selectedGrade}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {functions.map((func) => (
                      <div key={func.id}>
                        {/* Function Header */}
                        <div
                          className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => toggleFunction(func.id)}
                        >
                          {expandedFunctions[func.id] ? (
                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <FolderOpen className="w-4 h-4 text-[#14B8A6] flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800 truncate">
                              {func.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {func.topicsWithNotes}/{func.totalTopics} topics • {func.totalNotes} chapters
                            </div>
                          </div>
                        </div>
                        
                        {/* Topics Tree */}
                        {expandedFunctions[func.id] && func.topicTree?.length > 0 && (
                          <div className="ml-2">
                            {renderTopicTree(func.topicTree, func)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </Card>

          {/* Right Panel - Chapters */}
          <Card className="flex-1 flex flex-col">
            {selectedTopic ? (
              <>
                <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold">
                      {selectedTopic.name}
                    </CardTitle>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {topicNotes.reduce((sum, t) => sum + t.notes.length, 0)} chapters
                      {selectedTopicWithSubs?.children?.length > 0 && 
                        ` (including ${selectedTopicWithSubs.children.length} sub-topics)`}
                    </p>
                  </div>
                  <Button onClick={() => openNewNoteEditor()} size="sm" className="bg-[#14B8A6] hover:bg-[#0F766E]">
                    <Plus className="w-4 h-4 mr-1" /> Add Chapter
                  </Button>
                </CardHeader>
                <ScrollArea className="flex-1">
                  <div className="p-4">
                    {topicNotes.length === 0 || topicNotes.every(t => t.notes.length === 0) ? (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <h3 className="text-gray-600 font-medium mb-1">No chapters yet</h3>
                        <p className="text-sm text-gray-500 mb-4">
                          Create the first chapter for this topic
                        </p>
                        <Button onClick={() => openNewNoteEditor()} variant="outline">
                          <Plus className="w-4 h-4 mr-2" /> Create Chapter
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {topicNotes.map((topicGroup, groupIdx) => (
                          <div key={topicGroup.topicId}>
                            {/* Sub-topic header */}
                            {topicGroup.isSubTopic && (
                              <div className="flex items-center justify-between mb-3 pb-2 border-b">
                                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                  <ChevronRight className="w-4 h-4 text-[#14B8A6]" />
                                  {topicGroup.topicName}
                                </h3>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => {
                                    const subTopic = selectedTopicWithSubs?.children?.find(c => c.id === topicGroup.topicId);
                                    openNewNoteEditor(subTopic);
                                  }}
                                  className="text-[#14B8A6] h-7"
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Add
                                </Button>
                              </div>
                            )}
                            
                            {/* Chapters list */}
                            {topicGroup.notes.length > 0 ? (
                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={(e) => handleChapterDragEnd(e, topicGroup.topicId)}
                              >
                                <SortableContext
                                  items={topicGroup.notes.map(n => n.id)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  <div className="space-y-2" data-testid={`chapter-list-${topicGroup.topicId}`}>
                                    {topicGroup.notes
                                      .map((note, idx) => {
                                      const reportCount = flaggedCounts.by_note[note.id] || 0;
                                      const reports = noteReports[note.id] || [];
                                      const isReportsExpanded = expandedReports[note.id];
                                      
                                      return (
                                        <SortableChapterRow key={note.id} id={note.id}>
                                          <div className="border rounded-lg overflow-hidden bg-white">
                                      <div
                                        className="flex items-start gap-3 p-3 hover:bg-gray-50 transition-all cursor-pointer group"
                                        onClick={() => openEditNoteEditor(note)}
                                      >
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-600 flex-shrink-0">
                                          {idx + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <h4 className="font-medium text-gray-900 truncate">{note.title}</h4>
                                            <Badge className={`text-xs ${STATUS_COLORS[note.status]}`}>
                                              {STATUS_LABELS[note.status]}
                                            </Badge>
                                            {/* Flag count badge */}
                                            {reportCount > 0 && (
                                              <Badge 
                                                variant="outline" 
                                                className="text-xs border-red-300 text-red-600 bg-red-50"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (!isReportsExpanded) {
                                                    fetchNoteReports(note.id);
                                                  }
                                                  setExpandedReports(prev => ({ ...prev, [note.id]: !prev[note.id] }));
                                                }}
                                              >
                                                <Flag className="w-3 h-3 mr-1" />
                                                {reportCount}
                                              </Badge>
                                            )}
                                          </div>
                                          {note.excerpt && (
                                            <p className="text-sm text-gray-500 line-clamp-1">{note.excerpt}</p>
                                          )}
                                          {note.status === 'rejected' && note.rejection_reason && (
                                            <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-600">
                                              <AlertCircle className="w-3 h-3 inline mr-1" />
                                              {note.rejection_reason}
                                            </div>
                                          )}
                                          <div className="text-xs text-gray-400 mt-1">
                                            By {note.created_by_name} • {new Date(note.updated_at).toLocaleDateString()}
                                          </div>
                                        </div>
                                        
                                        {/* Quick Actions */}
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                                          {/* Copy inline link — pastes as [[note:UUID]] anywhere in a note body
                                              and renders as a live clickable link at read time. */}
                                          {note.status === 'published' && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(`[[note:${note.id}]]`)
                                                  .then(() => toast.success('Inline link copied'))
                                                  .catch(() => toast.error('Copy failed'));
                                              }}
                                              className="text-sky-600 h-8 w-8 p-0"
                                              title="Copy inline link"
                                              data-testid={`copy-inline-link-${note.id}`}
                                            >
                                              <Link2 className="w-4 h-4" />
                                            </Button>
                                          )}
                                          {/* Submit for Review (sub-admin) */}
                                          {isSubAdmin && (note.status === 'draft' || note.status === 'rejected') && note.created_by === user?.id && (
                                            <Button 
                                              variant="ghost" 
                                              size="sm"
                                              onClick={(e) => handleSubmitForReview(note, e)}
                                              className="text-amber-600 h-8 w-8 p-0"
                                              title="Submit for Review"
                                            >
                                              <Send className="w-4 h-4" />
                                            </Button>
                                          )}
                                          
                                          {/* Direct Publish (admin only for draft/rejected) */}
                                          {isFullAdmin && (note.status === 'draft' || note.status === 'rejected') && (
                                            <Button 
                                              variant="ghost" 
                                              size="sm"
                                              onClick={(e) => handleApproveNote(note, e)}
                                              className="text-green-600 h-8 w-8 p-0"
                                              title="Publish"
                                            >
                                              <Check className="w-4 h-4" />
                                            </Button>
                                          )}
                                          
                                          {/* Approve/Reject (admin for pending_review) */}
                                          {isFullAdmin && note.status === 'pending_review' && (
                                            <>
                                              <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={(e) => handleApproveNote(note, e)}
                                                className="text-green-600 h-8 w-8 p-0"
                                                title="Approve"
                                              >
                                                <Check className="w-4 h-4" />
                                              </Button>
                                              <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={(e) => { e.stopPropagation(); setNoteToReject(note); setRejectDialogOpen(true); }}
                                                className="text-red-600 h-8 w-8 p-0"
                                                title="Reject"
                                              >
                                                <X className="w-4 h-4" />
                                              </Button>
                                            </>
                                          )}
                                          
                                          {/* Delete (admin) */}
                                          {isFullAdmin && (
                                            <Button 
                                              variant="ghost" 
                                              size="sm"
                                              onClick={(e) => { e.stopPropagation(); setNoteToDelete(note); setDeleteConfirmOpen(true); }}
                                              className="text-red-500 h-8 w-8 p-0"
                                              title="Delete"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Expandable Reports Section */}
                                      {isReportsExpanded && reportCount > 0 && (
                                        <div className="border-t bg-red-50/50 p-3">
                                          <div className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
                                            <Flag className="w-3 h-3" />
                                            User Reports ({reports.length})
                                          </div>
                                          <div className="space-y-2">
                                            {reports.map((report) => (
                                              <div key={report.id} className="bg-white p-2 rounded border border-red-200 text-sm">
                                                <div className="flex items-start justify-between gap-2">
                                                  <div className="flex-1">
                                                    <span className="font-medium text-gray-700 capitalize">
                                                      {report.reason_type.replace('_', ' ')}
                                                    </span>
                                                    <span className="text-gray-400 text-xs ml-2">
                                                      by {report.user_name}
                                                    </span>
                                                    {report.description && (
                                                      <p className="text-gray-600 text-xs mt-1">{report.description}</p>
                                                    )}
                                                  </div>
                                                  <div className="flex gap-1">
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      onClick={() => updateReportStatus(report.id, 'resolved')}
                                                      className="h-7 px-2 text-green-600 hover:bg-green-50"
                                                    >
                                                      <Check className="w-3 h-3 mr-1" /> Resolve
                                                    </Button>
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      onClick={() => updateReportStatus(report.id, 'dismissed')}
                                                      className="h-7 px-2 text-gray-500 hover:bg-gray-100"
                                                    >
                                                      <X className="w-3 h-3 mr-1" /> Dismiss
                                                    </Button>
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                            {reports.length === 0 && (
                                              <div className="text-center text-gray-400 text-xs py-2">
                                                <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
                                                Loading reports...
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                        </SortableChapterRow>
                                  );
                                })}
                              </div>
                                </SortableContext>
                              </DndContext>
                            ) : topicGroup.isSubTopic ? (
                              <div className="text-center py-4 text-gray-400 text-sm">
                                No chapters in this sub-topic
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-1">Select a Topic</h3>
                  <p className="text-sm text-gray-500">
                    Choose a topic from the left panel to view or add chapters
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Chapter Editor Dialog */}
        <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingNote ? 'Edit Chapter' : 'New Chapter'}
                {(selectedSubTopicForNew || selectedTopic) && (
                  <span className="font-normal text-gray-500 text-sm ml-2">
                    in {selectedSubTopicForNew?.name || selectedTopic?.name}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Chapter Title *</Label>
                <Input
                  value={noteForm.title}
                  onChange={(e) => setNoteForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Enter chapter title..."
                />
              </div>

              <div>
                <Label>Content</Label>
                <RichTextEditor
                  content={noteForm.content}
                  onChange={(html) => setNoteForm(f => ({ ...f, content: html }))}
                  onImageSelect={handleImageSelect}
                  placeholder="Write your chapter content..."
                />
              </div>

              <div>
                <Label>Excerpt (optional)</Label>
                <Textarea
                  value={noteForm.excerpt}
                  onChange={(e) => setNoteForm(f => ({ ...f, excerpt: e.target.value }))}
                  placeholder="Brief description for search results..."
                  rows={2}
                />
              </div>

              {/* Also Read — grade-scoped, cross-function related-note picker.
                  Renders as a search field + checkbox list; selected notes
                  become the "Also Read" section on the reader page in the
                  order they were picked. Excludes self so a note can't
                  reference itself. */}
              <div className="rounded-md border bg-sky-50/30 p-3" data-testid="also-read-section">
                <Label className="text-sm font-semibold text-sky-900">Also Read (optional)</Label>
                <p className="text-xs text-gray-500 mb-2">
                  Suggest related chapters within the same grade — spans functions since knowledge crosses them.
                </p>
                {noteForm.related_note_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {noteForm.related_note_ids.map((rid, idx) => {
                      const meta = pickerNotes.find(n => n.id === rid);
                      return (
                        <span key={rid} className="inline-flex items-center gap-1 bg-white border border-sky-200 rounded-full px-2 py-0.5 text-xs">
                          <span className="text-gray-400">{idx + 1}.</span>
                          <span className="text-gray-800">{meta ? meta.title : rid}</span>
                          {meta?.function_name && <span className="text-gray-400">· {meta.function_name}</span>}
                          <button
                            type="button"
                            onClick={() => setNoteForm(f => ({ ...f, related_note_ids: f.related_note_ids.filter(id => id !== rid) }))}
                            className="text-gray-400 hover:text-red-500"
                            data-testid={`also-read-remove-${rid}`}
                          >×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <Input
                  value={pickerFilter}
                  onChange={(e) => setPickerFilter(e.target.value)}
                  placeholder="Type to filter notes..."
                  className="text-sm"
                  data-testid="also-read-filter"
                />
                <div className="max-h-40 overflow-y-auto border rounded mt-2 bg-white">
                  {pickerNotes
                    .filter(n => !pickerFilter || n.title.toLowerCase().includes(pickerFilter.toLowerCase()) || (n.function_name || '').toLowerCase().includes(pickerFilter.toLowerCase()))
                    .filter(n => !noteForm.related_note_ids.includes(n.id))
                    .slice(0, 40)
                    .map(n => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => setNoteForm(f => ({ ...f, related_note_ids: [...f.related_note_ids, n.id] }))}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-sky-50 border-b last:border-b-0"
                        data-testid={`also-read-option-${n.id}`}
                      >
                        <span className="text-gray-800">{n.title}</span>
                        {n.function_name && <span className="text-gray-400 ml-1">· {n.function_name}</span>}
                      </button>
                    ))
                  }
                  {pickerNotes.length === 0 && (
                    <p className="p-2 text-xs text-gray-400">No other notes available in this grade yet.</p>
                  )}
                </div>
              </div>

              {/* Move-to-topic — admin reassignment.
                  Only available for existing notes (no point on create:
                  the topic is fixed by the row admin clicked Add from). */}
              {editingNote && (
                <div className="rounded-md border bg-amber-50/40 p-3" data-testid="note-reassign-section">
                  {!showReassignPicker ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowReassignPicker(true);
                        setReassignTopicId(editingNote.topic_id);
                      }}
                      className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 font-medium"
                      data-testid="note-reassign-toggle"
                    >
                      <FolderInput className="w-4 h-4" />
                      Move this chapter to a different topic
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Move to topic</Label>
                        <button
                          type="button"
                          onClick={() => { setShowReassignPicker(false); setReassignTopicId(''); }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel reassignment
                        </button>
                      </div>
                      <TopicPickerTree
                        topics={flatTopicsForGrade.filter(
                          (t) => t.id !== editingNote.topic_id
                        )}
                        value={reassignTopicId}
                        onChange={(id) => setReassignTopicId(id || '')}
                        placeholder="Search a destination topic…"
                        allowClear
                        testIdPrefix="note-reassign-pick"
                      />
                      <p className="text-xs text-amber-700">
                        The chapter will be moved when you click Save. Choose a topic above to enable the move.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditorOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveNote} 
                disabled={saving}
                className="bg-[#14B8A6] hover:bg-[#0F766E]"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingNote ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pending Review Dialog */}
        <Dialog open={pendingReviewOpen} onOpenChange={setPendingReviewOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Pending Review
              </DialogTitle>
            </DialogHeader>
            
            {pendingReviews.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No chapters pending review
              </div>
            ) : (
              <ScrollArea className="max-h-[400px]">
                <div className="divide-y">
                  {pendingReviews.map((note) => (
                    <div 
                      key={note.id} 
                      className="py-3 px-2 hover:bg-gray-50 cursor-pointer rounded"
                      onClick={() => {
                        setPendingReviewOpen(false);
                        const func = functions.find(f => f.topics?.some(t => t.id === note.topic_id));
                        if (func) {
                          setExpandedFunctions(prev => ({ ...prev, [func.id]: true }));
                          const topic = func.topics?.find(t => t.id === note.topic_id);
                          if (topic) {
                            fetchTopicNotes(topic, func.topics);
                          }
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{note.title}</h4>
                          <div className="text-sm text-gray-500">
                            {note.grade} • {note.function_name} • {note.topic_name}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            By {note.created_by_name} • {new Date(note.submitted_at).toLocaleDateString()}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Chapter</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete &quot;{noteToDelete?.title}&quot;? This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteNote}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Chapter</DialogTitle>
              <DialogDescription>
                Provide a reason for rejecting &quot;{noteToReject?.title}&quot;
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Reason for rejection..."
              rows={3}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setRejectDialogOpen(false); setRejectionReason(''); }}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleRejectNote}>
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Image Picker Modal */}
        <ImagePickerModal
          isOpen={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImagePicked}
        />
      </div>
    </Layout>
  );
};

export default NotesManagementPage;
